import React, { useState } from "react";
import { GridApiPro } from "@mui/x-data-grid-pro";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { SearchSystems } from "../../graphql/queries/searchSystems";
import ExportMenuButton from "../ExportMenuButton";
import { readGridColumnLayout } from "../Table/gridColumnLayout";
import { ExportColumnDef, ExportFormat, exportRows } from "../../utils/listExport";
import { ExportMode, orderExportColumns } from "../../utils/tableColumnLayout";
import { buildDynamicListFilter } from "../../utils/buildDynamicListFilter";
import { buildInstalledBasePathString } from "../../utils/installedBasePath";
import { getSystemTypeLabel } from "../../config/systemTypes";
import { getIotStatusLabel } from "../../config/iotConnectionStatusConfig";
import { useSnackbar } from "../../provider/snackbar";
import { TypeDefinitionAttribute } from "../../types";

const EXPORT_PAGE_SIZE = 100;

// Same fields as the systems table (SystemsTable), in its default order
const getExportColumns = (t: TFunction): ExportColumnDef[] => [
    {
        field: "title",
        headerName: t("Title"),
        getValue: (row) => row.system?.title || "",
    },
    {
        field: "systemType",
        headerName: t("System Type"),
        getValue: (row) => getSystemTypeLabel(row.system?.systemType, t),
    },
    {
        field: "imei",
        headerName: t("IMEI"),
        getValue: (row) => row.system?.iotConnection?.gateway?.imei || "",
    },
    {
        field: "client",
        headerName: t("Company"),
        getValue: (row) => row.system?.client?.name || "",
    },
    {
        field: "location",
        headerName: t("Location"),
        // Full path, like the asset export; the table cell shows the parent only
        getValue: (row) => buildInstalledBasePathString(row.system?.installedBaseNode),
    },
    {
        field: "assetCount",
        headerName: t("Asset Count"),
        getValue: (row) => String(row.system?.assetList?.total ?? ""),
        getTypedValue: (row) => row.system?.assetList?.total,
    },
    {
        field: "connectionStatus",
        headerName: t("IoT System Status"),
        getValue: (row) =>
            getIotStatusLabel(row.system?.iotConnection?.connectionStatus, t, { isSystem: true, forExport: true }),
    },
];

interface Props {
    filter?: Record<string, any>;
    searchParams?: { query: string; fields: string[] };
    attributes?: TypeDefinitionAttribute[];
    /** The table's grid: the export columns follow its order and visibility. */
    apiRef?: React.MutableRefObject<GridApiPro>;
}

const SystemExportButton = ({ filter, searchParams, attributes, apiRef }: Props) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const snackbar = useSnackbar();
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = (mode: ExportMode, format: ExportFormat) => {
        // Read before the async fetch: the columns the user sees when clicking
        const exportColumns = orderExportColumns(getExportColumns(t), readGridColumnLayout(apiRef), mode);
        // Same variables as the table (SystemsTable), so the export covers exactly its rows
        const variables = {
            query: searchParams?.query
                ? { query: searchParams.query, fields: searchParams.fields }
                : undefined,
            filter: buildDynamicListFilter(filter ?? {}, (attributes ?? []).filter((a) => a.index !== "search")),
        };

        setIsExporting(true);
        snackbar.startLocalProgress(t("Preparing export..."));

        // Capture snackbar methods in closure so they survive component unmount
        const { updateLocalProgress, finishLocalProgress, errorLocalProgress } = snackbar;

        const fetchPage = (start: number) =>
            client.query({
                query: SearchSystems,
                variables: { ...variables, pagination: { start, limit: EXPORT_PAGE_SIZE } },
                fetchPolicy: "no-cache",
            });

        (async () => {
            try {
                const firstResult = await fetchPage(0);
                const total: number = firstResult.data.searchSystems.total;
                let allRows: any[] = [...firstResult.data.searchSystems.searchSystemResults];
                updateLocalProgress(allRows.length, total);

                for (let start = EXPORT_PAGE_SIZE; start < total; start += EXPORT_PAGE_SIZE) {
                    const result = await fetchPage(start);
                    allRows = [...allRows, ...result.data.searchSystems.searchSystemResults];
                    updateLocalProgress(allRows.length, total);
                }

                await exportRows({
                    format,
                    columns: exportColumns,
                    rows: allRows,
                    fileBaseName: "systems-export",
                    sheetName: t("Systems"),
                });

                finishLocalProgress(t("Export complete"));
            } catch (error) {
                console.error("System export failed:", error);
                errorLocalProgress(t("Export failed"));
            } finally {
                setIsExporting(false);
            }
        })();
    };

    return (
        <ExportMenuButton
            onExport={handleExport}
            disabled={isExporting}
            testId="system-export-button"
        />
    );
};

export default SystemExportButton;
